import { THEME_SWITCHER_IDS } from '../../config/uiPreferences';
import { TABLE_UI_THEMES, normalizeTableUiThemeId } from '../../config/tableUiTheme';

/** Labels aligned with ThemeContext switcher themes. */
const COLOR_THEME_LABELS = {
  qms_professional: 'QMS Professional',
  qms_clarity: 'QMS Clarity',
  qms_dark: 'QMS Dark',
  corporate_blue: 'Corporate Blue',
  professional_custom: 'Professional Custom',
};

export const UI_COLOR_THEME_OPTIONS = THEME_SWITCHER_IDS.map((id) => ({
  value: id,
  label: COLOR_THEME_LABELS[id] || id,
}));

export const UI_TABLE_UI_THEME_OPTIONS = ['legacy', 'refined'].map((id) => ({
  value: id,
  label: TABLE_UI_THEMES[id]?.labelEl || id,
}));

function yesNo(value) {
  return value === false ? 'Όχι' : 'Ναι';
}

function parsePrefsObject(entity) {
  const raw = entity?.uiPreferences;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw;
  return {};
}

/**
 * Flatten uiPreferences for list, form, and split view.
 * @param {object} entity
 */
export function enrichUserWithUiPreferences(entity) {
  if (!entity || typeof entity !== 'object') return entity;

  const prefs = parsePrefsObject(entity);

  const colorTheme =
    typeof prefs.colorTheme === 'string' ? prefs.colorTheme : '';
  const tableUiTheme =
    typeof prefs.tableUiTheme === 'string'
      ? normalizeTableUiThemeId(prefs.tableUiTheme)
      : '';

  const showSidebarGroups = prefs.showSidebarGroups !== false;
  const showSidebarSearch = prefs.showSidebarSearch !== false;
  const showSidebarFavorites = prefs.showSidebarFavorites !== false;

  return {
    ...entity,
    uiColorTheme: colorTheme,
    uiTableUiTheme: tableUiTheme,
    uiShowSidebarGroups: showSidebarGroups,
    uiShowSidebarSearch: showSidebarSearch,
    uiShowSidebarFavorites: showSidebarFavorites,
    uiColorThemeDisplay: colorTheme
      ? COLOR_THEME_LABELS[colorTheme] || colorTheme
      : '—',
    uiTableUiThemeDisplay: tableUiTheme
      ? TABLE_UI_THEMES[tableUiTheme]?.labelEl || tableUiTheme
      : '—',
    uiSidebarLayoutDisplay: [
      yesNo(showSidebarGroups),
      yesNo(showSidebarSearch),
      yesNo(showSidebarFavorites),
    ].join(' · '),
  };
}

/**
 * Build uiPreferences payload for user create/update (admin).
 * @param {object} data — form payload
 */
export function buildUiPreferencesPayload(data) {
  const hasColor = Object.prototype.hasOwnProperty.call(data, 'uiColorTheme');
  const hasTable = Object.prototype.hasOwnProperty.call(data, 'uiTableUiTheme');
  const hasGroups = Object.prototype.hasOwnProperty.call(data, 'uiShowSidebarGroups');
  const hasSearch = Object.prototype.hasOwnProperty.call(data, 'uiShowSidebarSearch');
  const hasFavorites = Object.prototype.hasOwnProperty.call(
    data,
    'uiShowSidebarFavorites'
  );

  if (!hasColor && !hasTable && !hasGroups && !hasSearch && !hasFavorites) {
    return null;
  }

  const base = { ...parsePrefsObject(data) };

  if (hasColor && data.uiColorTheme) {
    base.colorTheme = data.uiColorTheme;
  }
  if (hasTable && data.uiTableUiTheme) {
    base.tableUiTheme = normalizeTableUiThemeId(data.uiTableUiTheme);
  }
  if (hasGroups) {
    base.showSidebarGroups = Boolean(data.uiShowSidebarGroups);
  }
  if (hasSearch) {
    base.showSidebarSearch = Boolean(data.uiShowSidebarSearch);
  }
  if (hasFavorites) {
    base.showSidebarFavorites = Boolean(data.uiShowSidebarFavorites);
  }

  return Object.keys(base).length > 0 ? base : null;
}
